/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import { getUserLanguageCode } from 'js/lib/language';
import { useRetracked } from 'js/lib/retracked';

import type { Theme } from '@coursera/cds-core';
import { Button, Checkbox, CheckboxGroup, Dialog, TextField, Typography2 } from '@coursera/cds-core';
import { ThumbsDownFilledIcon, ThumbsUpFilledIcon } from '@coursera/cds-icons';

import { useGiveTranslationFeedback } from 'bundles/page-header/components/translation/useGiveTranslationFeedback';

import _t from 'i18n!nls/content-feedback';

import type {
  TranslationFeedback_ReasonForFeedback as ReasonForFeedback,
  TranslationFeedback_TrackingInfo as TranslationFeedbackTrackingInfo,
} from '../../../../__generated__/graphql-types';

const styles = {
  actionButton: ({ spacing }: Theme) => css`
    margin-right: ${spacing(16)};
  `,
  textFieldLabel: ({ spacing }: Theme) => css`
    margin-top: ${spacing(16)};
  `,
};

type Props = {
  open: boolean;
  onClose: () => void;
  onFeedbackSubmited?: () => void;
  trackingInfo?: TranslationFeedbackTrackingInfo;
};

type Feedback = '' | 'Yes' | 'No';

type SelectFeedbackTypeContentProps = {
  onGoodFeedback: () => void;
  onBadFeedback: () => void;
};
const SelectFeedbackTypeContent = ({ onGoodFeedback, onBadFeedback }: SelectFeedbackTypeContentProps) => {
  return (
    <>
      <Dialog.HeadingGroup>{_t('Is this translation accurate?')}</Dialog.HeadingGroup>
      <Dialog.Content>
        <Typography2 component="p" variant="bodyPrimary">
          {_t('Your feedback will be used to improve this translation for other learners.')}
        </Typography2>
      </Dialog.Content>
      <Dialog.Actions>
        <Button
          css={styles.actionButton}
          size="medium"
          variant="secondary"
          onClick={onGoodFeedback}
          icon={<ThumbsUpFilledIcon />}
        >
          {_t('Yes')}
        </Button>
        <Button
          css={styles.actionButton}
          size="medium"
          variant="secondary"
          onClick={onBadFeedback}
          icon={<ThumbsDownFilledIcon />}
        >
          {_t('No')}
        </Button>
      </Dialog.Actions>
    </>
  );
};

type AddFeedbackDetailsContentProps = {
  onGiveFeedback: (feedback: string, reasonsForFeedback: ReasonForFeedback[]) => void;
  onSkipFeedback: () => void;
};
const AddFeedbackDetailsContent = ({ onGiveFeedback, onSkipFeedback }: AddFeedbackDetailsContentProps) => {
  const [moreInfo, setMoreInfo] = useState('');
  const [reasonForFeedback, setReasonForFeedback] = useState<ReasonForFeedback[]>([]);

  return (
    <>
      <Dialog.HeadingGroup>{_t('Add translation feedback')}</Dialog.HeadingGroup>

      <Dialog.Content>
        <CheckboxGroup
          label={_t("What's wrong with this translation?")}
          value={reasonForFeedback}
          onChange={(event) => {
            const value = event.target.value as ReasonForFeedback;
            if (reasonForFeedback.includes(value)) {
              setReasonForFeedback(reasonForFeedback.filter((v) => v !== value));
            } else {
              setReasonForFeedback([...reasonForFeedback, value]);
            }
          }}
        >
          <Checkbox label={_t('It’s harmful or offensive')} value="HARMFUL_OFFENSIVE" />
          <Checkbox label={_t('It’s hard to understand')} value="HARD_TO_UNDERSTAND" />
          <Checkbox label={_t('Some parts of it aren’t translated')} value="PARTIAL_TRANSLATION" />
          <Checkbox label={_t('Something else')} value="OTHER" />
        </CheckboxGroup>
        <TextField
          css={styles.textFieldLabel}
          fullWidth
          label={_t('How could it be improved?')}
          placeholder={_t('Enter the text that has a problem or suggest a better translation.')}
          value={moreInfo}
          onChange={(e) => setMoreInfo(e.target.value)}
        />
      </Dialog.Content>

      <Dialog.Actions>
        <Button
          disabled={moreInfo.length === 0 || reasonForFeedback.length === 0}
          css={styles.actionButton}
          size="medium"
          variant="primary"
          onClick={() => {
            onGiveFeedback(moreInfo, reasonForFeedback);
          }}
        >
          {_t('Submit')}
        </Button>
        <Button
          css={styles.actionButton}
          size="medium"
          variant="secondary"
          onClick={() => {
            onSkipFeedback();
          }}
        >
          {_t('Skip')}
        </Button>
      </Dialog.Actions>
    </>
  );
};

const TranslationFeedbackDialog: React.FC<Props> = ({ open, trackingInfo, onClose, onFeedbackSubmited }) => {
  const locale = getUserLanguageCode();
  const giveFeedback = useGiveTranslationFeedback(trackingInfo);
  const [feedback, setFeedback] = useState<Feedback>('');
  const track = useRetracked();

  // make sure that the dialog is pristine when opening
  useEffect(() => {
    if (open) {
      setFeedback('');
    }
    // we are intentionally only doing this when open changes
  }, [open]);

  return (
    <Dialog open={open} variant="standard" onClose={onClose}>
      {!feedback && (
        <SelectFeedbackTypeContent
          onGoodFeedback={() => {
            track({
              trackingName: 'translation_feedback',
              action: 'feedback',
              trackingData: {
                likesTranslation: true,
                trackingInfo,
                locale,
              },
            });
            onClose();
            onFeedbackSubmited?.();
          }}
          onBadFeedback={() => {
            track({
              trackingName: 'translation_feedback',
              action: 'feedback',
              trackingData: {
                likesTranslation: false,
                trackingInfo,
                locale,
              },
            });
            setFeedback('No');
          }}
        />
      )}
      {feedback === 'No' && (
        <AddFeedbackDetailsContent
          onGiveFeedback={(detailedFeedback, reasonsForFeedback) => {
            giveFeedback(detailedFeedback, reasonsForFeedback);
            onClose();
            onFeedbackSubmited?.();
          }}
          onSkipFeedback={() => {
            onClose();
            onFeedbackSubmited?.();
          }}
        />
      )}
    </Dialog>
  );
};

export default TranslationFeedbackDialog;
